import { IAsset } from "entities/market";
import { sessionModel } from "entities/session";
import { sessionStore } from "entities/session/model/sessionModel";
import { MultiLangField } from "helpers/functions";
import moment from "moment";
import { toast } from "react-toastify";
import { ContentType } from "shared/api/http-client";
import { ResponseCodes, sircapApi } from "shared/api/sircap";
import {
  ChangePasswordDto,
  ChangePinCodeDto,
  ResetPinCodeDto,
  GenerateReportDto,
  RegisterEmailDto,
  RegisterPhoneDto,
  WithdrawRequestDto,
  DepositRequestDto,
  KycStepsDto,
  SubAccountDto,
} from "shared/api/sircap/Api";
import { EntityType } from "static/types";
import { fbTrackKycStatusTransition } from "utils/fbEvents";
import { createStore, useStore } from "zustand";
import { persist } from "zustand/middleware";

export enum KYC_Status {
  None = 0,
  Pending = 1,
  Rejected = 2,
  Success = 3,
}

// export enum KYC_Step {
//   None = 0,
//   Residence = 1,
//   Name = 2,
//   DateOfBirth = 4,
//   Address = 16,
//   // PhoneVerification = 17,
//   EmailVerification = 18,
//   PhoneVerification = 19,
//   // DocumentNumber = 19,
//   Document = 20,
//   Selfie = 21,
//   ProofOfAddress = 22,
// }

export enum KYC_Step {
  None = 0,
  Residence = 1,
  Name = 2,
  DateOfBirth = 4,
  Address = 16,
  EmailVerification = 18,
  PhoneVerification = 19,
  Documents = 20,
}

export enum KYB_Step {
  None = 0,
  Name = 2,
  // PhoneVerification = 17,
  EmailVerification = 18,
  PhoneVerification = 19,
  Documents = 23,
}

export interface IKycInfo {
  address_json: null | {
    street: string;
    building: string;
    city: string;
    zip_code: string;
    apartment: string;
  };
  user_id: number;
  step: KYC_Step;
  status: KYC_Status;
  reject_reason: string;
  is_armenia_resident: boolean;
  citizenship: string;
  gender: string;
  birth_date: string;
  personal_address: string;
  social_number: string;
  national_id: string;
  residence: string;
  has_american_citizenship: boolean;
  is_political_figure: boolean;
  documents: {
    PASSPORT: string;
    ID_CARD_FRONT_SIDE: string;
    ID_CARD_BACK_SIDE: string;
    INTERNAL_PASSPORT: string;
    SELFIE: string;
    UTILITY_BILL: string;
    RESIDENCE_PERMIT: string;
    OTHER_DOCUMENTS: Array<{ COUNTRY: string; DOCS: any }>;
  };
  end_datetime: string | null;
}

export interface IKybInfo {
  user_id: number;
  step: KYB_Step;
  status: KYC_Status;
  reject_reason: string;
  documents: any;
  additional_document: any;
  citizenship: string;
  residence?: string;
  personal_address: string;
  tin: string;
  yur_name: string;
  national_id: string;
}

export enum BonusType {
  Verification = "verification",
  Questionnaire = "questionnaire",
  Other = "other",
}

interface IBonusItem {
  id: number;
  points: number;
  type: BonusType;
  timer_hours: number | null;
  startTimer: string | null;
  active: boolean;
  activated: boolean;
  expired: boolean;
  automatically: boolean;
  manually: boolean;
  title: MultiLangField;
  description: MultiLangField;
}

interface IQuestion {
  alias: string;
  text: MultiLangField;
  type: "select" | "multi-select" | "text";
  options: Array<{ label: MultiLangField; value: string }>;
  info: MultiLangField;
}

interface IDepositInfo {
  id: number;
  currency: { code: string };
  accountNumber: string;
  recipient: string;
  info_source: any;
}

interface IWithdrawInfo {
  id: number;
  user: number;
  entity_type: string;
  amount: number;
  currency: { code: string; symbol: string };
  account_number: string;
  info_source: any;
}

interface IActivity {
  id: number;
  t: "deposit" | "withdrawal" | "payment";
  type: "deposit" | "withdrawal" | "commission" | "payment";
  status: number;
  amount: number;
  currency: string;
  createdAt: string;
  updatedAt: string;
  // Deposits/withdrawals only — when the money was credited/sent.
  settlement_date?: string | null;
  comment: string;
  asset: {
    symbol: string;
  };
}

interface ITransaction {}

interface IReport {
  from_date: string;
  to_date: string;
  filename: string;
  type: string;
  user_identificator: string;
  order_id: number;
  /** Готовая подписанная ссылка на файл — склеивать путь руками нельзя. */
  url: string;
}

export interface KycStartDto {}

interface IAssignmentInfo {
  type: string;
  id: string;
  document: any;
  title: MultiLangField;
  description: MultiLangField;
  form: any[];
}

interface IAnalyticsMetadata {
  first_order: boolean;
  first_deposit: boolean;
}

export enum RepoPaymentType {
  Daily = "daily",
  Monthly = "monthly",
  Yearly = "yearly",
  OneTime = "one_time",
}

export interface IDepositPayment {
  id: number;
  currency: string;
  fix_amount: number;
  expected_profit?: number; // total profit
  paid_percent?: number; // total history profit
  current_profit: number;
  percent: number;
  start_date: string;
  end_date: string;
  paid?: boolean;
  payment_type?: RepoPaymentType;
  assignment_id: number;
  assignment: IAssignmentInfo;
}

export interface IRepoDeal {
  id: number;
  start_date: string;
  end_date: string;
  rate: number;
  repo_type: string;
  currency: string;
  quantity: number;
  quantity_under_repo: number;
  received_amount: number;
  nominal_value: number;
  comment: string;
  counterparty_id: number;
  deal_type_id: number;
  asset_id: number;
  asset: IAsset;
  assignment_id: number;
  assignment: IAssignmentInfo;
}

interface IParsedData {
  first_name: string;
  last_name: string;
  original_first_name: string;
  original_last_name: string;
  document_number: string;
  birth_date: string;
}

export enum UserSubAccountType {
  Brokerage = "brokerage",
  Margin = "margin",
  TrustManagement = "trust_management",
}

export interface IUserSubAccount {
  id: number;
  name: string;
  sub_number: string;
  type: UserSubAccountType;
}

type AccountState = {
  isLoading: boolean;
  requestedEmail: string;
  requestedPhone: string;
  depositInfo: IDepositInfo[];
  withdrawInfo: IWithdrawInfo[];
  depositCurrencies: { code: string }[];
  selectedDepositCurrency: string;
  selectedWithdrawCurrency: string;

  uploadAvatar: (file: File, t: any, cb: any) => void;
  removeAvatar: (t: any, cb: any) => void;
  changePassword: (body: ChangePasswordDto, t: any, cb: any) => void;
  changePinCode: (body: ChangePinCodeDto, t: any, cb: any) => void;
  resetPinCode: (body: ResetPinCodeDto) => Promise<null | any>;

  requestChangeEmail: (
    body: Partial<RegisterEmailDto>,
    t: any,
    cb: any,
  ) => void;
  confirmChangeEmail: (
    body: { code: string },
    t: any,
    cb: any,
    catchCb: any,
  ) => void;
  requestChangePhone: (
    body: Partial<RegisterPhoneDto>,
    t: any,
    cb: any,
  ) => void;
  confirmChangePhone: (
    body: { code: string },
    t: any,
    cb: any,
    catchCb: any,
  ) => void;

  getDepositInfo: () => void;
  loadVerification: () => any;
  getWithdrawInfo: () => void;
  requestWithdrawal: (data: WithdrawRequestDto, cb: any) => void;
  requestDeposit: (data: DepositRequestDto, cb: any) => void;
  cancelWithdrawal: (id: number, cb: any) => void;
  selectCurrency: (type: "deposit" | "withdraw") => (curr: string) => void;
  getContract: () => Promise<any>;
  getIccContract: (amount: number) => Promise<any>;
  getDocuments: (type: string) => Promise<any>;
  requestDataDeletion: (t: any, pinCode: string) => Promise<any>;
  signContract: (pinCode: string, t: any, onSuccess: any) => Promise<any>;
  signIccContract: (
    pinCode: string,
    contractId: number,
    t: any,
    onSuccess: any,
  ) => Promise<any>;

  activites: IActivity[];
  activitesCount: number;
  transactions: any[];
  transactionsCount: number;
  currencyActivities: any[];
  currencyActivitiesCount: number;

  activeActivites: IActivity[];
  activeActivitesCount: number;
  activeTransactions: any[];
  activeTransactionsCount: number;

  getActivities: (page: number, limit: number) => void;
  getTransactions: (page: number, limit: number) => void;
  getActiveTransactions: (page: number, limit: number) => void;
  getCurrencyActivities: (
    currency: string,
    page: number,
    limit: number,
  ) => void;

  reports: IReport[];
  getReports: () => Promise<void>;
  generateReport: (dto: GenerateReportDto) => Promise<string>;

  promoCode: (value: string) => Promise<any>;

  kyc: IKycInfo | null;
  kyb: IKybInfo | null;

  synckyc: (kyc: IKycInfo) => void;
  synckyb: (kyb: IKybInfo) => void;

  startKyc: (dto: KycStartDto) => any;
  startKyb: (dto: KycStartDto) => any;
  stepKyc: (dto: KycStepsDto) => any;
  stepKyb: (dto: KycStepsDto) => any;

  questionnaireStatus: KYC_Status | null;
  questionnaire: {
    questionnaire: Array<IQuestion>;
    status: KYC_Status;
    answers: any;
  } | null;
  loadQuestionnaire: () => void;
  submitQuestionnaire: (data: any) => void;

  bonusInfo: {
    balance: number;
    tasks: IBonusItem[];
  } | null;

  getBonusInfo: () => Promise<void>;
  activateBonus: (id: number, err: any) => Promise<void>;

  assignments: any[];
  assignmentsPending: any[];
  assignmentsHistory: any[];
  assignment: IAssignmentInfo | null;
  assignmentContract: any | null;
  getAssignments: () => Promise<void>;
  getAssignmentsPending: () => Promise<void>;
  getAssignmentsHistory: (type?: string) => Promise<void>;
  getAssignment: (type: string) => Promise<void>;
  submitAssignment: (data: any) => Promise<any>;
  getAssignmentContractId: (id: string) => Promise<void>;
  signAssignmentContract: (
    pinCode: string,
    t: any,
    onSuccess: any,
    id: number,
  ) => Promise<any>;
  cancelAssignmentContract: (
    pinCode: string,
    t: any,
    onSuccess: any,
    id: number,
  ) => Promise<any>;

  depositPayments: IDepositPayment[];
  depositPaymentsCount: number;
  getDepositPayments: () => Promise<void>;

  repoDeals: IRepoDeal[];
  repoDealsHistory: IRepoDeal[];
  getRepoDeals: () => Promise<void>;

  analyticsMetadata: IAnalyticsMetadata | null;
  getAnalyticsMetadata: () => Promise<void>;

  residentInfoArray: any[];
  setResidentInfoArray: (value: any[] | ((prev: any[]) => any[])) => void;
  only_armenia_resident: boolean | null;
  setOnlyArmeniaResident: (value: boolean | null) => void;

  modalOpened: number | null;
  setModalOpened: (value: number | null) => void;

  parsModalOpened: boolean;
  setParsModalOpened: (value: boolean) => void;
  parsedData: IParsedData | null;
  setParsedData: (value: IParsedData | null) => void;
  removeParsedData: () => void;

  subAccounts: IUserSubAccount[];
  getSubAccounts: () => Promise<void>;
  createSubAccount: (dto: SubAccountDto) => Promise<void>;
  updateSubAccount: (id: number, dto: SubAccountDto) => Promise<void>;
  deleteSubAccount: (id: number) => Promise<void>;
};

export const accountStore = createStore<AccountState>()(
  persist(
    (set, get) => ({
      isLoading: false,
      requestedEmail: "",
      requestedPhone: "",
      depositInfo: [],
      depositCurrencies: [],
      selectedDepositCurrency: "",
      selectedWithdrawCurrency: "",
      withdrawInfo: [],

      uploadAvatar: async (file: File, t: any, cb: any) => {
        const promise = new Promise<void>(async (res, rej) => {
          const resp = await sircapApi.account.uploadAvatar(
            { avatar: file },
            { type: ContentType.FormData },
          );

          if (resp.error) {
            // switch (resp.error.code) {
            //   case ResponseCodes.ServerError:
            //     toast.error(t(String(resp.error.code)));
            //     break;
            //   default:
            //     break;
            // }
          } else {
            cb(resp.data.data);
            res();
          }
        });

        toast.promise(promise, {
          pending: "Uploading",
          error: "Server Error",
          success: "Success",
        });
      },
      removeAvatar: async (t: any, cb: any) => {
        const promise = new Promise<void>(async (res, rej) => {
          const resp = await sircapApi.account.removeAvatar();

          if (resp.error) {
            rej();
          } else {
            cb(resp.data.data);
            res();
          }
        });

        toast.promise(promise, {
          pending: "Deleting",
          error: "Server Error",
          success: "Success",
        });
      },
      changePassword: async (body: ChangePasswordDto, t: any, cb: any) => {
        const promise = new Promise<void>(async (res, rej) => {
          const resp = await sircapApi.account.changePass(body);

          if (resp.error) {
            rej();
          } else {
            cb();
            res();
          }
        });

        toast.promise(promise, {
          pending: "Pending",
          error: "Server Error",
        });
      },
      changePinCode: async (body: ChangePinCodeDto, t: any, cb: any) => {
        set({ isLoading: true });
        const promise = new Promise<void>(async (res, rej) => {
          const resp = await sircapApi.account.changePinCode(body);

          set({ isLoading: false });
          if (resp.error) {
            rej();
          } else {
            cb(true);
            res();
          }
        });

        toast.promise(promise, {
          pending: "Pending",
          error: "Wrong code",
        });
      },

      resetPinCode: async (body: ResetPinCodeDto) => {
        set({ isLoading: true });
        try {
          const resp = await sircapApi.account.resetPinCode(body);

          set({ isLoading: false });
          if (resp.error) {
            return null;
          } else {
            return resp.data.data?.pin || null;
          }
        } catch (error) {
          return null;
        }
      },
      getContract: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.getContract();
          return resp;
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      signContract: async (pinCode: string, t: any, onSuccess: any) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.signContract({
            pinCode,
          });
          if (resp.error) {
            toast.error(t(resp.error.code));
          } else {
            onSuccess();
            toast.success("Success");
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      getIccContract: async (amount: number) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.previewIccContract({
            params: {
              amount,
            },
          });
          return resp;
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      signIccContract: async (
        pinCode: string,
        contractId: number,
        t: any,
        onSuccess: any,
      ) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.signIccContract({
            pinCode,
            contractId,
          });
          if (resp.error) {
            toast.error(t(resp.error.code));
          } else {
            onSuccess();
            toast.success("Success");
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      getDocuments: async (type: string) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.getDocuments({
            params: {
              type,
            },
          });
          return resp.data;
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      requestDataDeletion: async (t: any, pinCode: string) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.requestDataDeletion({
            pinCode,
          });
          if (resp.error) {
            toast.error(t(resp.error.code));
          } else {
            toast.success("Success");
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      requestChangeEmail: (
        body: Partial<RegisterEmailDto>,
        t: any,
        cb: any,
      ) => {
        set({ isLoading: true });
        const promise = new Promise<void>(async (res, rej) => {
          const resp = await sircapApi.account.requestChangeEmail(body);

          set({ isLoading: false });
          if (resp.error) {
            rej();
          } else {
            set({ requestedEmail: body.email });
            cb();
            res();
          }
        });

        toast.promise(promise, {
          pending: "Pending",
          error: "Server Error",
        });
      },
      confirmChangeEmail: (
        body: { code: string },
        t: any,
        cb: any,
        catchCb: any,
      ) => {
        const promise = new Promise<void>(async (res, rej) => {
          const resp = await sircapApi.account.confirmChangeEmail(body);

          if (resp.error) {
            catchCb();
            rej();
          } else {
            cb();
            res();
          }
        });

        toast.promise(promise, {
          pending: "Pending",
          error: "Server Error",
        });
      },
      requestChangePhone: (
        body: Partial<RegisterPhoneDto>,
        t: any,
        cb: any,
      ) => {
        set({ isLoading: true });
        const promise = new Promise<void>(async (res, rej) => {
          const resp = await sircapApi.account.requestChangePhone(body);

          set({ isLoading: false });
          if (resp.error) {
            if (resp.error.code === ResponseCodes.EmailIsUsed) {
              toast.error("Number already in use");
            } else {
              toast.error("Server error");
            }
            rej();
          } else {
            set({ requestedPhone: body.phone });
            cb();
            res();
          }
        });

        toast.promise(promise, {
          pending: "Pending",
        });
      },
      confirmChangePhone: (
        body: { code: string },
        t: any,
        cb: any,
        catchCb: any,
      ) => {
        const promise = new Promise<void>(async (res, rej) => {
          const resp = await sircapApi.account.confirmChangePhone(body);

          if (resp.error) {
            catchCb();
            rej();
          } else {
            cb();
            res();
          }
        });

        toast.promise(promise, {
          pending: "Pending",
          error: "Server Error",
        });
      },

      getDepositInfo: async () => {
        set({ isLoading: true });
        const resp = await sircapApi.transfer.depositInfo();

        if (resp.error) {
        } else {
          const data = resp.data.data;
          set({ depositInfo: data });
          try {
            const currencies: any[] = data.map((v: any) => v.currency);
            set({ depositCurrencies: currencies });
            const selected = get().selectedDepositCurrency;
            if (selected) {
              if (
                !(data as Array<any>).find((v) => v.currency?.code === selected)
              ) {
                set({ selectedDepositCurrency: currencies[0].code });
              }
            } else {
              set({ selectedDepositCurrency: currencies[0].code });
            }
          } catch (error) {}
        }
        set({ isLoading: false });
      },
      loadVerification: async () => {
        // set({isLoading: true});
        const resp = await sircapApi.onboarding.verificationLoad();

        if (resp.error) {
          return null;
        } else {
          const data = resp.data.data;
          return data;
        }
        // set({isLoading: false});
      },
      getWithdrawInfo: async () => {
        set({ isLoading: true });
        try {
          const resp = await sircapApi.transfer.withdrawInfo();

          if (resp.error) {
          } else {
            const data = resp.data.data;
            set({ withdrawInfo: data });
            if (data) {
              const currencies: any[] = data.map((v: any) => v.currency.code);
              const selected = get().selectedWithdrawCurrency;
              if (selected) {
                if (!currencies.includes(selected)) {
                  set({ selectedWithdrawCurrency: currencies[0] });
                }
              } else {
                set({ selectedWithdrawCurrency: currencies[0] });
              }
            }
          }
        } catch (error) {}
        set({ isLoading: false });
      },
      requestWithdrawal: async (data: WithdrawRequestDto, cb: any) => {
        set({ isLoading: true });
        const resp = await sircapApi.transfer.withdrawRequest(data);

        cb(resp);
        set({ isLoading: false });
      },
      requestDeposit: async (data: DepositRequestDto, cb: any) => {
        set({ isLoading: true });
        const resp = await sircapApi.transfer.depositRequest(data);

        cb(resp);
        set({ isLoading: false });
      },
      cancelWithdrawal: async (id: number, cb: any) => {
        set({ isLoading: true });
        const resp = await sircapApi.transfer.withdrawCancel({ id });

        if (resp.error) {
          toast.error("Server Error");
        } else {
          cb(resp.data);
        }
        set({ isLoading: false });
      },
      selectCurrency: (type: "deposit" | "withdraw") => (curr: string) => {
        if (type === "deposit") {
          set({ selectedDepositCurrency: curr });
        } else {
          set({ selectedWithdrawCurrency: curr });
        }
      },

      startKyc: async (dto: KycStartDto) => {
        set({ isLoading: true });
        try {
          const resp = await sircapApi.account.startKyc(dto);
          set({ isLoading: false });
          return resp;
        } catch (error) {
          set({ isLoading: false });
          toast.error("Server Error");
        }
      },
      startKyb: async (dto: KycStartDto) => {
        set({ isLoading: true });
        try {
          const resp = await sircapApi.account.startKyb(dto);
          set({ isLoading: false });
          return resp;
        } catch (error) {
          set({ isLoading: false });
          toast.error("Server Error");
        }
      },

      stepKyc: async (dto: KycStepsDto) => {
        set({ isLoading: true });
        try {
          const resp = await sircapApi.account.stepKyc(dto);
          set({ isLoading: false });
          return resp;
        } catch (error) {
          set({ isLoading: false });
          toast.error("Server Error");
        }
      },

      stepKyb: async (dto: KycStepsDto) => {
        set({ isLoading: true });
        try {
          const resp = await sircapApi.account.stepKyb(dto);
          set({ isLoading: false });
          return resp;
        } catch (error) {
          set({ isLoading: false });
          toast.error("Server Error");
        }
      },

      kyc: null,
      kyb: null,

      synckyc: (kyc: IKycInfo) => {
        const prevStatus = get().kyc?.status;
        set({ kyc });
        if (kyc) {
          fbTrackKycStatusTransition(prevStatus, kyc.status);
        }
      },
      synckyb: (kyb: IKybInfo) => {
        const prevStatus = get().kyb?.status;
        const isLoad = get().isLoading;
        set({ isLoading: true });
        set({ kyb });
        set({ isLoading: isLoad });
        if (kyb) {
          fbTrackKycStatusTransition(prevStatus, kyb.status);
        }
      },

      questionnaireStatus: null,
      questionnaire: null,

      loadQuestionnaire: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.questionLoad({});
          if (resp.error) {
          } else {
            const data = resp.data.data;
            set({ questionnaire: data });
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      submitQuestionnaire: async (data: any) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.questionSubmit(data);
          if (resp.error) {
          } else {
            toast.success("Success");
            set({ questionnaireStatus: KYC_Status.Pending });
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },

      bonusInfo: null,

      getBonusInfo: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.bonusInfo();
          set({ bonusInfo: resp.data.data });
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },

      activateBonus: async (id: number, err: any) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.bonusActivate(id)({});
          if (resp.error) {
            console.log(resp.error);
            toast.error(err(String(resp.error.code)));
          } else {
            get().getBonusInfo();
            toast.success("Success");
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },

      transactions: [],
      transactionsCount: 0,
      activeActivites: [],
      activeActivitesCount: 0,
      activeTransactions: [],
      activeTransactionsCount: 0,
      activites: [],
      activitesCount: 0,
      currencyActivities: [],
      currencyActivitiesCount: 0,
      getActivities: async (page: number, limit: number) => {
        try {
          const isNewTab = page === 0;
          set({ isLoading: isNewTab });
          const resp = await sircapApi.account.activities({
            params: {
              page,
              limit,
            },
          });

          if (resp.error) {
          } else {
            const [list, count] = resp.data.data;
            if (isNewTab) {
              set({ activites: list, activitesCount: count });
            } else {
              const lastAssets = get().activites;
              set({
                activites: [...lastAssets, ...list],
                activitesCount: count,
              });
            }
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      getTransactions: async (page: number, limit: number) => {
        try {
          const isNewTab = page === 0;
          set({ isLoading: isNewTab });
          const resp = await sircapApi.account.transactions({
            params: {
              page,
              limit,
            },
          });

          if (resp.error) {
          } else {
            const [list, count] = resp.data.data;
            if (isNewTab) {
              set({ transactions: list, transactionsCount: count });
            } else {
              const lastAssets = get().transactions;
              set({
                transactions: [...lastAssets, ...list],
                transactionsCount: count,
              });
            }
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      getActiveTransactions: async (page: number, limit: number) => {
        try {
          const isNewTab = page === 0;
          set({ isLoading: isNewTab });
          const resp = await sircapApi.account.activeTransactions({
            params: {
              page,
              limit,
            },
          });

          if (resp.error) {
          } else {
            const [list, count] = resp.data.data;
            if (isNewTab) {
              set({ activeTransactions: list, activeTransactionsCount: count });
            } else {
              const lastAssets = get().activeTransactions;
              set({
                activeTransactions: [...lastAssets, ...list],
                activeTransactionsCount: count,
              });
            }
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },

      getCurrencyActivities: async (
        currency: string,
        page: number,
        limit: number,
      ) => {
        try {
          const isNewTab = page === 0;
          set({ isLoading: isNewTab });
          const resp = await sircapApi.account.currencyActivities({
            params: {
              currency,
              page,
              limit,
            },
          });

          if (resp.error) {
          } else {
            const [list, count] = resp.data.data;
            if (isNewTab) {
              set({
                currencyActivities: list,
                currencyActivitiesCount: count,
              });
            } else {
              const lastAssets = get().currencyActivities;
              set({
                currencyActivities: [...lastAssets, ...list],
                currencyActivitiesCount: count,
              });
            }
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },

      reports: [],
      getReports: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.reportsList();
          set({ reports: resp.data.data.list });
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      generateReport: async (dto: GenerateReportDto) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.generateReport(dto);
          return resp.data.data.fileName;
        } catch (error) {
          return null;
        } finally {
          set({ isLoading: false });
        }
      },

      promoCode: async (value: string) => {
        const resp = await sircapApi.account.promoCode(value)({});
        return resp;
      },

      assignments: [],
      assignmentsPending: [],
      assignmentsHistory: [],
      assignment: null,
      assignmentContract: null,
      getAssignments: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.assignments({});

          if (resp.error) {
          } else {
            const list = resp.data.data;
            set({ assignments: list });
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },

      getAssignment: async (type: string) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.getAssignment(type)();

          if (resp.error) {
          } else {
            const type = resp.data.data;
            set({ assignment: type });
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      getAssignmentsPending: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.assignmentsPending({});

          if (resp.error) {
          } else {
            const list = resp.data.data;
            set({ assignmentsPending: list });
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      getAssignmentsHistory: async (type?: string) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.getAssignmentsHistory({
            params: type ? { type: type } : {},
          });

          if (resp.error) {
          } else {
            const [list] = resp.data.data;
            set({ assignmentsHistory: list });
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      submitAssignment: async (data: any) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.assignmentSubmit(data);
          if (resp.error) {
            return null;
          } else {
            return resp.data.data;
          }
        } catch (error) {
          return null;
        } finally {
          set({ isLoading: false, assignmentContract: null });
        }
      },
      getAssignmentContractId: async (id: string) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.assignmentContract(id)();

          if (resp.error) {
          } else {
            const data = resp.data.data;
            set({ assignmentContract: data });
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      signAssignmentContract: async (
        pinCode: string,
        t: any,
        onSuccess: any,
        id: number,
      ) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.assignmentContractSigned(id)({
            pinCode,
          });
          if (resp.error) {
            toast.error(t(resp.error.code));
          } else {
            onSuccess();
            toast.success("Success");
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      cancelAssignmentContract: async (
        pinCode: string,
        t: any,
        onSuccess: any,
        id: number,
      ) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.assignmentContractCanceled(id)({
            pinCode,
          });
          if (resp.error) {
            toast.error(t(resp.error.code));
          } else {
            onSuccess();
            toast.success("Success");
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },

      depositPayments: [],
      depositPaymentsCount: 0,
      getDepositPayments: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.depositPayments();
          const [list, count] = resp.data.data;
          set({ depositPayments: list, depositPaymentsCount: count });
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },

      repoDeals: [],
      repoDealsHistory: [],
      getRepoDeals: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.repoDeals();
          const deals = resp.data.data;
          const activeDeals = deals.filter((item: IRepoDeal) => {
            return moment(item.end_date).isAfter(moment());
          });
          const historyDeals = deals.filter((item: IRepoDeal) => {
            return moment(item.end_date).isBefore(moment());
          });
          set({ repoDeals: activeDeals, repoDealsHistory: historyDeals });
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },

      analyticsMetadata: null,
      getAnalyticsMetadata: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.analyticsMetadata();
          set({ analyticsMetadata: resp.data.data });
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },

      residentInfoArray: [],
      setResidentInfoArray: (value) => {
        if (typeof value === "function") {
          const updater = value as (prev: any[]) => any[];
          set({ residentInfoArray: updater(get().residentInfoArray) });
        } else {
          set({ residentInfoArray: value });
        }
      },

      only_armenia_resident: null,
      setOnlyArmeniaResident: (value) => set({ only_armenia_resident: value }),

      modalOpened: null,
      setModalOpened: (value) => set({ modalOpened: value }),

      parsModalOpened: false,
      setParsModalOpened: (value) =>
        set({
          parsModalOpened: value,
        }),

      parsedData: null,
      setParsedData: (value) => set({ parsedData: value }),
      removeParsedData: () => set({ parsedData: null }),

      subAccounts: [],
      getSubAccounts: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.subAccounts();
          if (resp.error) {
          } else {
            const list = resp.data.data;
            set({ subAccounts: list });
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      createSubAccount: async (dto: SubAccountDto) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.createSubAccount(dto);
          if (resp.error) {
            toast.error("Server Error");
          } else {
            await get().getSubAccounts();
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      updateSubAccount: async (id: number, dto: SubAccountDto) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.updateSubAccount(id)(dto);
          if (resp.error) {
            toast.error("Server Error");
          } else {
            await get().getSubAccounts();
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      deleteSubAccount: async (id: number) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.account.deleteSubAccount(id)();
          if (resp.error) {
            toast.error("Server Error");
          } else {
            await get().getSubAccounts();
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: "account",
      version: 1,
      merge: (persistedState, currState) => {
        const {
          requestedEmail,
          requestedPhone,
          depositCurrencies,
          selectedDepositCurrency,
          selectedWithdrawCurrency,
          residentInfoArray,
          only_armenia_resident,
          modalOpened,
          parsModalOpened,
          parsedData,
        } = persistedState as Partial<AccountState>;

        return {
          ...currState,

          requestedEmail: requestedEmail || "",
          requestedPhone: requestedPhone || "",
          selectedDepositCurrency: selectedDepositCurrency || "",
          selectedWithdrawCurrency: selectedWithdrawCurrency || "",
          depositCurrencies: depositCurrencies?.length ? depositCurrencies : [],
          residentInfoArray: residentInfoArray || [],
          only_armenia_resident: only_armenia_resident || null,
          modalOpened: modalOpened || null,
          parsModalOpened: parsModalOpened || false,
          parsedData: parsedData || null,
        };
      },
    },
  ),
);

export const useDepositInfo = (currency: string, bank: number | null) =>
  useStore(accountStore, (state) =>
    state.depositInfo.find(
      (v) =>
        (currency ? v.currency.code === currency : true) &&
        (bank ? v.info_source.id === bank : true),
    ),
  );

export const useWithdrawalInfo = (
  currency: string,
  bank: number | string | null,
  // accountNumber: number | null
) =>
  useStore(accountStore, (state) =>
    state.withdrawInfo.find(
      (v) =>
        (currency ? v.currency.code === currency : true) &&
        (bank ? v.info_source.id === bank : true),
      //  &&
      // (accountNumber ? v.info_source.id === accountNumber : true)
    ),
  );

export const useUserKYCStatus = () =>
  useStore(accountStore, (state) => {
    if (sessionStore.getState().entity === EntityType.Individual) {
      return state.kyc?.status;
    } else {
      return state.kyb?.status;
    }
  });
